import "@/app/style/videoPlayer.scss";
import { Box, Grid, SxProps, Theme } from '@mui/material';
import React, { use, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { adobeData, theme } from "@/app/utils/Common";
import screenfull from 'screenfull';
import { PlayCircle } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { VideoSource } from "@/app/utils/class/common/video";
import Control from "./control";
import { textSize } from "@/app/utils/MixinSize";
import { LANGTYPE_EN, useLanguage } from "@/app/utils/LanguageContext";
import { useMyContext } from "@/app/utils/MyContext";
import ReplayIcon from "@/app/ui/Common/SvgComponent/ReplayIcon";
export type VideoWAData = Pick<WAData, "raceNo" | "contentTitle">

export interface PlayerProps {
  sources: VideoSource[];
  open: boolean;
  title?: string;
  isMobile: boolean;
  handleClose: ()=> void;
  autoPlay?: boolean;
  showCurrentTooltip?: boolean;
  showHoveredTooltip?: boolean;
  width?: string; // work for desktop only
  height?: string; // work for desktop only
  isEmbed?: boolean;
  titleSx?: SxProps<Theme>;
  WAData?: VideoWAData;
  overridedContentSlot?: React.ReactNode; // will override player if any
  type?: string; // imageWithVideo | onlyImage
  trackImageSrc?: string;
}

const initialValue = {
  playing: false,
  muted: false,
  volume: 0.5,
  played: 0,
  playbackRate: 1.0,
  seeking: false,
  buffer : true,
  ended: false,
  loaded: 0,
};

let count = 0;
let playCount = 0;

const LANDSCAPE = 'landscape';
const PORTRAIT = 'portrait';

const Player = (props: PlayerProps) => {
  const {state: myContextState} = useMyContext();
  const {state: lang} = useLanguage();
  const { t } = lang;
  const [orientation, setOrientation] = useState(screen?.orientation?.type || (window.innerWidth > window.innerHeight ? LANDSCAPE : PORTRAIT));
  const controlRef = useRef<HTMLDivElement | null>(null);
  const videoPlayerRef = useRef<ReactPlayer>(null);
  const playerContainerRef = useRef<HTMLDivElement | null>(null);
  const [videoState, setVideoState] = useState({...initialValue, selectedSourceIdx: 0});
  const [fullscreen, setFullscreen] = useState(false);
  const [showErrMsg, setShowErrMsg] = useState(false);
  const [sendWA, setSendWA] = useState(false);
  const {selectedSourceIdx, playing: isPlaying, ended, muted, volume, playbackRate, played, seeking, buffer, loaded} = videoState;
  const [displayVideo, setdisplayVideo] = useState(false);
  const [displaySlot, setdisplaySlot] = useState(true);
  useEffect(() => {
    const handleOrientationChange= () => setOrientation(screen?.orientation?.type || (window.innerWidth > window.innerHeight ? PORTRAIT : LANDSCAPE)); // as it is called before rotate, therefore need to reverse the result to get the actual orientation
    const handleFullscreenChange = () => setFullscreen(document.fullscreenElement ? true : false);
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('fullscreenchange', handleFullscreenChange);
    }
  }, []);
  
  useEffect(() => {
    const defaultVideoIdx = props.sources?.findIndex((s) => s.keyName === (props.isMobile ? "sd" : "hd")) || -1
    setVideoState({ ...videoState, selectedSourceIdx: defaultVideoIdx === -1 ? 0: defaultVideoIdx});
    resetWAInfo();
  }, [props.open])

  const resetWAInfo = () => {
    playCount = 0;
    setSendWA(window.globalConfig?.WA?.VIDEO_PLAYED_SEC && window.globalConfig?.WA?.VIDEO_PLAYED_SEC != -1 && (props.WAData ? true : false))
  }
  
  const currentTime = videoPlayerRef.current ? videoPlayerRef.current.getCurrentTime() : 0;
  const duration = videoPlayerRef.current? videoPlayerRef.current.getDuration() : 0;

  const onCloseHandler = (e: any) => {
    e.preventDefault();
    if (props.handleClose)
      props.handleClose();
    setVideoState({...videoState, ...initialValue})
    setFullscreen(false);
    setShowErrMsg(false);
  }
  
  const mouseMoveHandler = () => {
    if (!controlRef.current) return;
    controlRef.current.style.visibility = "visible";
    count = 0;
  };

  // video
  const progressHandler = (state: any) => {
    if (count > 3) {
      if (!controlRef.current) return
      controlRef.current.style.visibility = "hidden"; // toggling player control container
      count = 0;
    } else if (controlRef.current && controlRef.current.style.visibility === "visible") {
      count += 1;
    }

    // WA
    if (videoState.playing && !videoState.buffer) {
      playCount += 1;
    }
    if (sendWA && playCount > window.globalConfig?.WA?.VIDEO_PLAYED_SEC) {
      // adobeAnalysis
      adobeData(lang.langType === LANGTYPE_EN ? "en" : "zh-hk", "interaction", {
        pageId: "racing/simulcast",
        eventId: "racing/simulcast_ClickPlayVideo",
        raceNo: props.WAData?.raceNo,
        videoFileName: videoPlayerRef.current?.getInternalPlayer().src,
        contentTitle: props.WAData?.contentTitle,
        videoSource: window.location.href,
        buttonName: "racing/simulcast_ClickVideo_CommonMenu",
        country: myContextState.selectedMeeting?.countryNameEn || ''
      });
      setSendWA(false);
    }

    if (!seeking) {
      setVideoState({ ...videoState, ...state});
    }
  };
  const bufferStartHandler = () => setVideoState({ ...videoState, buffer: true });
  const bufferEndHandler = () => setVideoState({ ...videoState, buffer: false });
  const readyHandler = () => setVideoState({...videoState, buffer: false, playing: Boolean(props.autoPlay) });
  const startHandler = () => {
    const playHolder = document.getElementById("trackAnalysisThumbnail") as any;
    if (playHolder) playHolder.style["display"] = "none";
    videoPlayerRef.current?.seekTo(videoState.played);
  }
  const endedHandler = () => setVideoState({...videoState, playing: false, ended: true});
  const errorHandler = (e: any) => {setShowErrMsg(true); console.error('video player err:', e)};
  
  //control
  const playPauseHandler = (e: any) => {
    // console.log('videoPlayerRef.current----', videoPlayerRef.current, ended, isPlaying, videoState)
    e.preventDefault();
    if (ended) {
      if (!videoPlayerRef.current) return
      videoPlayerRef.current.seekTo(videoPlayerRef.current.getCurrentTime()-videoPlayerRef.current.getCurrentTime());
      setVideoState(Object.assign({}, { ...videoState, playing: true, ended: false}));
    } else {
      setVideoState({ ...videoState, playing: !isPlaying});
    }
  };
  const seekHandler = (value: any) => setVideoState({ ...videoState, seeking: true, played: parseFloat(value) / 100 });
  const seekMouseUpHandler = (value: any ) => {
    if (!videoPlayerRef.current) return
    videoPlayerRef.current.seekTo(value / 100);
    setVideoState({ ...videoState, seeking: false, ended: false });
  };
  const toggleMute = () => setVideoState({ ...videoState, muted: !muted });
  const volumeChangeHandler = (e: any, value: string) => {
    e.preventDefault();
    const newVolume = parseFloat(value) / 100;
    setVideoState({...videoState, volume: newVolume, muted: Number(newVolume) === 0 ? true : false});
  };
  const volumeSeekUpHandler = (e: any, value: string) => {
    e.preventDefault();
    const newVolume = parseFloat(value) / 100;
    setVideoState({...videoState, volume: newVolume, muted: newVolume === 0 ? true : false});
  };
  const changeSourceHandler = (idx: number) => {
    if (idx !== selectedSourceIdx) {
      setVideoState({ ...videoState, selectedSourceIdx: idx});
      resetWAInfo();
    }
  };
  const toggleScreenOrientation = () => {
    // @ts-ignore
    if (!fullscreen && screen?.orientation?.lock) {
      // @ts-ignore
      screen?.orientation?.lock("landscape-primary")
        .catch((err: any) => {
          if (err.name && err.name === 'NotAllowedError') {
            console.error('User did not grant permission to lock screen orientation');
          } else {
            console.error('Error locking screen orientation:', err);
          }
        });
    } else if (screen?.orientation?.unlock) {
      screen?.orientation?.unlock();
    }
  };
  const toggleFullscreenMode = () => {
    if (!fullscreen) {
      if (!playerContainerRef.current) return;
      if (screenfull.isEnabled) {
        screenfull.request(playerContainerRef.current);
      } else if (playerContainerRef.current?.requestFullscreen) { // standard fullscreen api
        playerContainerRef.current?.requestFullscreen();
        // @ts-ignore
      } else if (playerContainerRef.current?.webkitRequestFullscreen) { // chrome/safari
        // @ts-ignore
        playerContainerRef.current?.webkitRequestFullscreen();
        // @ts-ignore
      } else if (playerContainerRef.current?.mozRequestFullscreen) { // firefox
        // @ts-ignore
        playerContainerRef.current?.mozRequestFullscreen();
        // @ts-ignore
      } else if (playerContainerRef.current?.msRequestFullscreen) { // IE/edge
        // @ts-ignore
        playerContainerRef.current?.msRequestFullscreen();
      } else if (videoPlayerRef.current?.getInternalPlayer()?.webkitEnterFullscreen) { // iphone safari
        videoPlayerRef.current?.getInternalPlayer()?.webkitEnterFullscreen();
        setVideoState({...videoState, playing: false});
        return false;
      } else if (videoPlayerRef.current?.getInternalPlayer()?.webkitEnterFullScreen) { // iphone safari
        videoPlayerRef.current?.getInternalPlayer()?.webkitEnterFullScreen();
        setVideoState({...videoState, playing: false});
        return false;
      }
    } else {
      if (screenfull.isFullscreen) {
        screenfull.exit();
      } else if (document.fullscreenElement) {
        document.exitFullscreen();
        // @ts-ignore
      } else if (document.webkitFullscreenElement) {
        // @ts-ignore
        document.webkitExitFullscreen();
        // @ts-ignore
      } else if (document.mozFullscreenElement) {
        // @ts-ignore
        document.mozExitFullscreen();
        // @ts-ignore
      } else if (document.msFullscreenElement) {
        // @ts-ignore
        document.msExitFullscreen();
      }
    }
    return true;
  };
  const toggleFullscreen = () => {
    const updateFullscreen = toggleFullscreenMode(); // iphone use system default control, not using our custom control
    if (props.isMobile) toggleScreenOrientation();
    if (updateFullscreen) setFullscreen(!fullscreen);
  };

  const commonStyle = () => ({
    width: (props.isMobile ? (orientation.toLowerCase().startsWith(LANDSCAPE) ? '100%' : '100%') : props.width) || '100%',
    height: (props.isMobile ? (orientation.toLowerCase().startsWith(LANDSCAPE) ? '100%' : (videoPlayerRef.current ? (props.height || 'auto') : (props.height || '25%'))) : props.height) || '100%',
  })

  const renderPlayer = () => 
  showErrMsg ?  
  (
    <Box sx={{width: '100%', height: '100%'}}>
      <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '100%',
            height: '100%',
            padding: props.isMobile ? '16px' : '24px',
            overflowY: 'auto',
            ...textSize(13),
        }}>
            <div style={{minHeight: props.isMobile ? '193px' : '258px', height: '100%', alignContent: 'center', textAlign: 'center'}}>{t("video_err_msg", '')}</div>
            <div className="linebreak" style={{borderTop: '1px solid', paddingTop: '10px', marginTop: '10px'}}>{t("video_err_remarks", '')?.replaceAll('{0}', ' • ')}</div>
        </div>
    </Box>
  ) : <>
    <>
    <ReactPlayer 
      ref={videoPlayerRef} //updating the react player ref
      url={props.sources && props.sources.length > selectedSourceIdx && props.sources[selectedSourceIdx] ? props.sources[selectedSourceIdx].url : ""}
      height="100%"
      width="100%"
      playing={isPlaying}
      volume={volume}
      muted={muted}
      onProgress = {progressHandler}
      onBuffer={bufferStartHandler}
      onBufferEnd={bufferEndHandler}
      onReady={readyHandler}
      onStart={startHandler}
      onEnded={endedHandler}
      onError={(e) => errorHandler(e)}
      playsinline
      playbackRate={1.5}
      />
      </>
    {
      // showErrMsg ?  
      // (
      //   <Box className="screenContainer">
      //     <span style={{color: "white"}}>video load failed</span>
      //   </Box>
      // ):
      (
        buffer ?
        <Box className="screenContainer">
          <CircularProgress disableShrink />
        </Box> :
        <Box className="screenContainer" sx={{opacity: isPlaying ? 0: 1}} onClick={(e) => playPauseHandler(e)}>
          <PlayCircle />
        </Box>
      )
    }
  </>


  const renderonlyImage = () => {
    return <div className="videoContainer_trackContent"><img  src={props?.trackImageSrc}></img></div>
  }
  const rendeimageWithVideo = () => {
    return <div className="videoContainer_trackContent">
        <img id={"trackAnalysisThumbnail"}src={props?.trackImageSrc}/>
            <p onClick={() => {
              setdisplaySlot(false)
              setdisplayVideo(true)
              setVideoState({ ...videoState, playing: true})
            }} style={{fontWeight: props.isMobile? 500 : 700}}>{t("Track_Video_text")} <ReplayIcon /></p>
        </div>
  }

  return (
    <Box
        sx={props.isEmbed ? {
          position: "relative",
          ...commonStyle()
        } :{
          position: 'fixed',
          top: props.isMobile && !displayVideo? 'calc(50% - 75px)': '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: "unset",
          zIndex: 999,
          ...commonStyle(),
          padding: "0 8px",
          }}>
      <Grid ref={playerContainerRef} container sx={{ 
        color: 'white',
        height: "100%",
        width: "100%",
        alignItems: 'baseline', 
        position: 'relative',
        alignContent: "flex-start"
        }}>
        <Grid className="title" item xs={12} sx={{
          height:"40px",
          backgroundColor: props.title ? theme.palette.primary.main : 'transparent',
          zIndex: 1,
          ...props.titleSx
        }}>
          {
            props.title && (<div id="modal-title" style={{
              maxHeight: '100%',
              fontWeight: 500,
              ...textSize(15),
              lineHeight: "28px",
              textAlign: 'center',
              marginTop: '6px',
              width: 'calc(100% - 30px)',
              display: '-webkit-box',
              '-webkit-line-clamp': '1',
              '-webkit-box-orient': 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',

            } as React.CSSProperties}>
              {props.title}
            </div>)
          }
          <Box className={`closeBtn`} onClick={(e) => onCloseHandler(e)} style={{ top: 5}} >
            <img src={`${myContextState.assetPrefix}/close.svg`} />
          </Box>
        </Grid>
        <Grid item columns={12} id="video" className="videoContainer_track" onMouseMove={mouseMoveHandler} sx={{
          background: displayVideo ? "#2f2f2f !important": "#fff",
          height:  displayVideo ? "calc( 100% - 40px )": "unset",
        }}>

        { props?.type == 'onlyImage' && displaySlot ? renderonlyImage() : null }
        { props?.type == 'imageWithVideo' && displaySlot ? rendeimageWithVideo() : null }
          {displayVideo && renderPlayer()}
        </Grid>
        
        <Grid item columns={12} className="controlWrapper" style={{display: displayVideo? "block" : "none"}}>
          <Control
            ref={controlRef}
            isMobile={props.isMobile}
            sources={props.sources}
            isPlaying={isPlaying}
            ended={ended}
            currentTime={currentTime}
            duration={duration}
            played={played}
            loaded={loaded}
            mute={muted}
            volume={volume}
            selectedSourceIdx={selectedSourceIdx}
            fullscreen={fullscreen}
            onPlayPause={playPauseHandler}
            onSeek={seekHandler}
            onSeekMouseUp={seekMouseUpHandler}
            onToggleMute={toggleMute}
            onVolumeChangeHandler={volumeChangeHandler}
            onVolumeSeekUp={volumeSeekUpHandler}
            onChangeSource={changeSourceHandler}
            onToggleFullscreen={toggleFullscreen}
            showCurrentTooltip={props.showCurrentTooltip}
            showHoveredTooltip={props.showHoveredTooltip} />
        </Grid>
        
      </Grid>
    </Box>
  )
}

export default Player;